/**
 * VolatileStorage is a manager class for accessing a Storage object (https://developer.mozilla.org/en-US/docs/Web/API/Storage).
 * In the case of the Storage source running out of space, the most stale data will be deleted to make room.
 * Data stored in browser storage is accessible by anyone using the machine.  DO NOT STORE SENSITIVE DATA.
 * Data stored in this storage is NOT GUARANTEED to come back.  Use CAUTIOUSLY.
 * @global
 * @typedef {Object} VolatileStorage
 * @property {function(string,object):undefined} set Set the [key, value] pair in storage
 * @property {function(string):object} get Retrieve the value at the specified key
 * @property {function(string):object} remove Remove and return the value at the specified key
 */

import { VolatileStorageManager } from "./types";

/**
* Returns a managed wrapper for Empty Storage - For use in simulating when browser storage isn't available.  For example, in incognito mode.
* Do NOT use in production.
* @param {String} cacheName The name of the cache of information being stored, used to avoid key collisions between different sets of data.
* @returns {VolatileStorage} Manager access for Empty Storage
*/
export let getEmptyStorage = function (cacheName: string)
{
    return buildVolatileStorage(buildEmptyStorage(), cacheName);
};

/**
* Returns a managed wrapper for Local Storage
* @param {String} cacheName The name of the cache of information being stored, used to avoid key collisions between different sets of data.
* @returns {VolatileStorage} Manager access for window.LocalStorage
*/
export let getLocalStorage = function (cacheName: string)
{
    return buildVolatileStorage(localStorageIsSupported() ? window.localStorage : buildEmptyStorage(), cacheName);
};

/**
* Returns a managed wrapper for Session Storage
* @param {String} cacheName The name of the cache of information being stored, used to avoid key collisions between different sets of data.
* @returns {VolatileStorage} Manager access for window.SessionStorage
*/
export let getSessionStorage = function (cacheName: string)
{
    return buildVolatileStorage(sessionStorageIsSupported() ? window.sessionStorage : buildEmptyStorage(), cacheName);
};

function buildVolatileStorage<T>(source: Storage, cacheName: string): VolatileStorageManager<T>
{
    const DEFAULT_CACHE_NAME = "VS";
    const ADDITIONAL_CLEAR_SIZE = 2000; // Size is in number of characters
    const PREFIX_POSTFIX_DELIMITER = '-';
    const TIMESTAMP_POSTFIX = "ts";

    const _source = source || buildEmptyStorage();
    const _cacheName = cacheName || DEFAULT_CACHE_NAME;
    let _debug = false; // Set to true for debug logging statements

    const vStorage: VolatileStorageManager<T> = {} as VolatileStorageManager<T>;

    vStorage.set = function (key, value)
    {
        var prefixedKey = getPrefixedKey(key);

        var setValueSuccessful = trySet(prefixedKey, value);
        if (!setValueSuccessful)
        {
            return;
        }

        var timestampKey = getTimestampKey(prefixedKey);
        var timestamp = new Date().getTime();

        var setTimestampSuccessful = trySet(timestampKey, timestamp);
        if (!setTimestampSuccessful)
        {
            remove(prefixedKey);
            return;
        }
    };

    vStorage.get = function (key)
    {
        var prefixedKey = getPrefixedKey(key);

        var value = getItem(prefixedKey);
        if (value === null)
        {
            return value;
        }

        var timestampKey = getTimestampKey(prefixedKey);
        var timestamp = new Date().getTime();

        var setTimestampSuccessful = trySet(timestampKey, timestamp);
        if (!setTimestampSuccessful)
        {
            remove(prefixedKey);
            return null;
        }

        return value;
    };

    vStorage.remove = function (key)
    {
        var prefixedKey = getPrefixedKey(key);
        return remove(prefixedKey);
    };

    return vStorage;

    // Accessors
    function trySet(key: string, value: any): boolean
    {
        if (typeof key !== "string" || key === "" || value === undefined || value === null)
        {
            // We cannot store unexpected keys or undefined values.  Null is the default value.
            return false;
        }

        var stringifiedValue;
        try
        {
            stringifiedValue = JSON.stringify(value);
        }
        catch (e)
        {
            // If the value cannot be stringified, we cannot store it
            return false;
        }

        try
        {
            debugWrite(`Attempting Set: [Key: ${key}] [Length: ${stringifiedValue.length}]`);
            _source.setItem(key, stringifiedValue);
        }
        catch (e)
        {
            if (!isStorageFullException(e))
            {
                // Some unexpected error has happened.
                return false;
            }

            debugWrite("Storage Full: Attempting to Clear Data");

            var spaceToClear = stringifiedValue.length + ADDITIONAL_CLEAR_SIZE;
            debugWrite(`Required Data To Clear: ${spaceToClear}`);

            if (!tryRemoveOldData(spaceToClear))
            {
                // We couldn't clear enough data to fit the value
                debugWrite("Could Not Clear Enough Space");
                return false;
            }

            debugWrite("Space Cleared: Attempting Set");

            return trySet(key, value);
        }

        return true;
    }
    function getItem(key: string): T | undefined
    {
        var value = _source.getItem(key);

        try
        {
            return JSON.parse(value!);
        }
        catch (e)
        {
            return;
        }
    }
    function remove(key: string)
    {
        var timestampKey = getTimestampKey(key);
        var value = _source.getItem(key);

        if (_debug) // Avoid calling getItem needlessly unless we're debugging
        {
            var length = value != null ? value.length : "null";
            debugWrite(`Removing Key: ${key} of Size: ${length} At Timestamp: ${getItem(timestampKey)}`);
        }

        _source.removeItem(key);
        _source.removeItem(timestampKey);

        return value;
    }

    // Helpers
    function isStorageFullException(ex: Error)
    {
        // https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
        return ex instanceof DOMException && (
            // Everything except firefox uses code 22
            ex.code === 22 ||
            // Firefox uses code 1014
            ex.code === 1014 ||
            // In case code wasn't set...
            // Everything except FireFox uses a version of 'Quota Exceeded Error' for name
            ex.name === 'QUOTA_EXCEEDED_ERR' ||
            ex.name === 'QuotaExceededError' ||
            // Firefox uses 'NS_ERROR_DOM_QUOTA_REACHED'
            ex.name === 'NS_ERROR_DOM_QUOTA_REACHED'
        );
    }
    function tryRemoveOldData(spaceToClear: number)
    {
        var keyTimestampPairs = getKeyTimestampPairs();

        // Sort so the oldest pair is at the end
        keyTimestampPairs.sort(function (pair1, pair2) { return pair2!.timestamp - pair1!.timestamp; });

        var spaceLeftToClear = spaceToClear;

        while (keyTimestampPairs.length > 0 && spaceLeftToClear > 0)
        {
            var currentPair = keyTimestampPairs.pop()!;
            var value = remove(currentPair.key)!;

            spaceLeftToClear -= value.length;
            spaceLeftToClear -= JSON.stringify(currentPair.timestamp).length;
        }

        return spaceLeftToClear <= 0;
    }
    function getKeyTimestampPairs()
    {
        var fullPostfixLength = PREFIX_POSTFIX_DELIMITER.length + TIMESTAMP_POSTFIX.length;
        var allKeys = Object.keys(_source);
        var keyTimestampPairs: { key: string, timestamp: number; }[] = [];

        for (var i = 0; i < allKeys.length; i++)
        {
            var timestampKey = allKeys[i];
            if (!timestampKey.endsWith(TIMESTAMP_POSTFIX))
            {
                continue;
            }

            var keyTimestampPair =
            {
                key: timestampKey.substring(0, timestampKey.length - fullPostfixLength),
                timestamp: getItem(timestampKey)! as unknown as number,
            };

            keyTimestampPairs.push(keyTimestampPair);
        }

        return keyTimestampPairs;
    }
    function debugWrite(message: string)
    {
        if (_debug) { console.info(message); }
    }

    // Key Handling
    function getPrefixedKey(key: string)
    {
        return _cacheName + PREFIX_POSTFIX_DELIMITER + key;
    }
    function getTimestampKey(prefixedKey: string)
    {
        return prefixedKey + PREFIX_POSTFIX_DELIMITER + TIMESTAMP_POSTFIX;
    }
}

function buildEmptyStorage(): Storage
{
    var emptyStorage: Storage = {} as Storage;
    // Properties
    (emptyStorage as any).length = 0;
    // Methods
    emptyStorage.clear = returnNothing;
    emptyStorage.getItem = returnNull;
    emptyStorage.key = returnNull;
    emptyStorage.removeItem = returnNothing;
    emptyStorage.setItem = returnNothing;

    return emptyStorage;

    function returnNothing() { }
    function returnNull() { return null; }
}

function localStorageIsSupported()
{
    try
    {
        return 'localStorage' in window && window['localStorage'] !== null;
    }
    catch (e)
    {
        return false;
    }
}

function sessionStorageIsSupported()
{
    try
    {
        return 'sessionStorage' in window && window['sessionStorage'] !== null;
    }
    catch (e)
    {
        return false;
    }
}
